import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle, FaRedo, FaTimes } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { cn, destructiveOutlineBtnClass } from "@/lib/utils";
import { HomePhaseShell } from "@/components/v2/home/HomePhaseShell";
import { MatchResultAdminForm } from "@/components/v2/home/HomePostMatchCard";

interface HomePostKickoffCardProps {
  isAdmin: boolean;
  scoreA: string;
  scoreB: string;
  winner: "A" | "B" | "Draw" | null;
  onScoreAChange: (value: string) => void;
  onScoreBChange: (value: string) => void;
  onWinnerChange: (value: "A" | "B" | "Draw" | null) => void;
  hasExistingResult: boolean;
  onLogResult: () => void;
  savingResult?: boolean;
  canDispute?: boolean;
  onDispute?: () => void;
  disputing?: boolean;
  showStaleBanner: boolean;
  onSkipStale: () => void;
  skipped: boolean;
  onStartNextWeek: () => void;
  startingNextWeek: boolean;
  onCancelMatch?: () => void;
  cancellingMatch?: boolean;
  historySlot?: ReactNode;
  commentsSlot?: ReactNode;
}

export function HomePostKickoffCard({
  isAdmin,
  scoreA,
  scoreB,
  winner,
  onScoreAChange,
  onScoreBChange,
  onWinnerChange,
  hasExistingResult,
  onLogResult,
  savingResult = false,
  canDispute = false,
  onDispute,
  disputing = false,
  showStaleBanner,
  onSkipStale,
  skipped,
  onStartNextWeek,
  startingNextWeek,
  onCancelMatch,
  cancellingMatch = false,
  historySlot,
  commentsSlot,
}: HomePostKickoffCardProps) {
  const { t } = useTranslation();

  if (skipped) {
    return (
      <HomePhaseShell
        eyebrow={t("v2.home.phaseEyebrowPostKickoff")}
        title={t("v2.home.staleSkippedTitle")}
        secondary={
          commentsSlot || historySlot ? (
            <div className="space-y-4">
              {commentsSlot}
              {historySlot}
            </div>
          ) : undefined
        }
        adminSection={
          isAdmin ? (
            <div className="space-y-3">
              <Button
                onClick={onStartNextWeek}
                disabled={startingNextWeek}
                className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
              >
                <FaRedo className="mr-1.5" />
                {startingNextWeek ? t("common.loading") : t("v2.home.startNextWeek")}
              </Button>
              {onCancelMatch && (
                <Button
                  type="button"
                  variant="outline"
                  className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
                  disabled={cancellingMatch}
                  onClick={onCancelMatch}
                >
                  <FaTimes className="mr-1.5 opacity-70" />
                  {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
                </Button>
              )}
            </div>
          ) : undefined
        }
        adminZone={
          isAdmin
            ? {
                title: t("v2.home.adminZoneNextWeekTitle"),
                description: t("v2.home.adminZoneNextWeekDesc"),
                actionRequired: true,
              }
            : undefined
        }
      >
        <p className="text-sm text-muted-foreground">
          {isAdmin ? t("v2.home.staleSkippedDesc") : t("v2.home.adminOnlyStartWeek")}
        </p>
      </HomePhaseShell>
    );
  }

  return (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowPostKickoff")}
      title={t("v2.home.postKickoffTitle")}
      meta={<span>{t("v2.home.postKickoffDesc")}</span>}
      secondary={
        commentsSlot || historySlot ? (
          <div className="space-y-4">
            {commentsSlot}
            {historySlot}
          </div>
        ) : undefined
      }
      adminSection={
        isAdmin ? (
          <div className="space-y-4">
            {showStaleBanner && (
              <div className="rounded-xl border border-amber-400/60 bg-amber-400/10 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-bold text-sm">{t("v2.home.staleResultTitle")}</p>
                    <p className="text-xs text-muted-foreground">{t("v2.home.staleResultDesc")}</p>
                  </div>
                </div>
                <Button onClick={onSkipStale} variant="outline" size="sm" className="w-full min-h-[44px]">
                  {t("v2.home.skipAndContinue")}
                </Button>
              </div>
            )}

            <MatchResultAdminForm
              scoreA={scoreA}
              scoreB={scoreB}
              winner={winner}
              onScoreAChange={onScoreAChange}
              onScoreBChange={onScoreBChange}
              onWinnerChange={onWinnerChange}
              onSave={onLogResult}
              saveLabel={hasExistingResult ? t("v2.home.updateResult") : t("v2.home.saveResult")}
              saving={savingResult}
            />

            {onCancelMatch && (
              <Button
                type="button"
                variant="outline"
                className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
                disabled={cancellingMatch}
                onClick={onCancelMatch}
              >
                <FaTimes className="mr-1.5 opacity-70" />
                {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
              </Button>
            )}
          </div>
        ) : undefined
      }
      adminZone={
        isAdmin
          ? {
              title: t("v2.home.adminZoneResultTitle"),
              description: t("v2.home.adminZoneResultDesc"),
              actionRequired: true,
            }
          : undefined
      }
    >
      {isAdmin ? (
        <div className="text-center py-4 space-y-2">
          <p className="font-semibold">{t("v2.home.awaitingOfficialResult")}</p>
          <p className="text-sm text-muted-foreground">{t("v2.home.awaitingOfficialResultDesc")}</p>
        </div>
      ) : (
        <div className="text-center py-4 space-y-3">
          <p className="font-semibold">{t("v2.home.awaitingOfficialResult")}</p>
          <p className="text-sm text-muted-foreground">{t("v2.home.awaitingOfficialResultDesc")}</p>
          {hasExistingResult && canDispute && onDispute && (
            <Button variant="outline" onClick={onDispute} disabled={disputing} className="min-h-[44px]">
              {disputing ? t("common.loading") : t("v2.home.disputeResult")}
            </Button>
          )}
        </div>
      )}
    </HomePhaseShell>
  );
}
